// Preset preload service — batch hydrate/analyze with concurrency control
// Phase 2: Hot cache preloading + stale detection

import type { Preset, SnapshotMeta } from './preset-types';
import * as store from './preset-store';

// ==================== Stale Detection ====================

/** Default stale threshold: 2 hours */
const DEFAULT_STALE_MS = 2 * 60 * 60 * 1000;

export interface StaleConfig {
  /** Max age in ms before a snapshot is considered stale (default: 2h) */
  maxAgeMs?: number;
  /** Rolling presets are checked against their window — if the resolved range
   *  has drifted past the snapshot's resolvedTo, it's stale */
  checkRollingDrift?: boolean;
}

export type FreshnessStatus = 'fresh' | 'stale' | 'expired' | 'none';

export interface FreshnessInfo {
  status: FreshnessStatus;
  ageMs: number;
  label: string;
  meta?: SnapshotMeta;
}

/** Check if a snapshot is stale based on age and time range drift */
export function checkFreshness(
  meta: SnapshotMeta | undefined,
  preset: Preset,
  config: StaleConfig = {}
): FreshnessInfo {
  if (!meta) {
    return { status: 'none', ageMs: 0, label: 'No snapshot' };
  }

  const maxAge = config.maxAgeMs ?? DEFAULT_STALE_MS;
  const now = Date.now();
  const analysedAt = new Date(meta.analysedAt).getTime();
  const ageMs = now - analysedAt;

  // Expired: > 3x stale threshold
  if (ageMs > maxAge * 3) {
    return { status: 'expired', ageMs, label: formatAge(ageMs) + ' old', meta };
  }

  // Rolling range drift check: if the preset is rolling and the snapshot's
  // resolvedTo is older than the current window start, it's stale
  if (config.checkRollingDrift !== false && preset.timeRange.type === 'rolling') {
    const windowMs = preset.timeRange.hours * 60 * 60 * 1000;
    const currentWindowStart = now - windowMs;
    const snapshotTo = new Date(meta.resolvedTo).getTime();
    // If the snapshot's end time is before the current window start, definitely stale
    if (snapshotTo < currentWindowStart) {
      return { status: 'stale', ageMs, label: 'Window drifted', meta };
    }
  }

  // Age-based stale check
  if (ageMs > maxAge) {
    return { status: 'stale', ageMs, label: formatAge(ageMs) + ' old', meta };
  }

  return { status: 'fresh', ageMs, label: formatAge(ageMs) + ' ago', meta };
}

function formatAge(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

// ==================== Preload Queue ====================

export type PreloadStatus = 'idle' | 'hydrating' | 'analyzing' | 'done' | 'failed';

export interface PreloadItem {
  presetId: string;
  status: PreloadStatus;
  progress: number;
  error?: string;
}

export interface PreloadProgress {
  total: number;
  completed: number;
  items: Map<string, PreloadItem>;
}

type AnalyzeFn = (preset: Preset) => Promise<void>;
type ProgressCallback = (progress: PreloadProgress) => void;

const MAX_CONCURRENT = 2;

/**
 * Preload multiple presets into the hot cache.
 * - If a warm (IndexedDB) snapshot exists and is fresh → hydrate into hot cache
 * - If no snapshot or stale → call analyzeFn to run analysis (which saves snapshot)
 * - Respects max concurrency for analysis jobs
 */
export async function preloadPresets(
  presets: Preset[],
  analyzeFn: AnalyzeFn,
  onProgress: ProgressCallback,
  staleConfig?: StaleConfig,
  signal?: AbortSignal
): Promise<PreloadProgress> {
  const items = new Map<string, PreloadItem>();
  for (const p of presets) {
    items.set(p.id, { presetId: p.id, status: 'idle', progress: 0 });
  }

  const progress: PreloadProgress = { total: presets.length, completed: 0, items };
  const emit = () => onProgress({ ...progress, items: new Map(items) });
  emit();

  // Phase 1: Try hydrating from warm cache (fast, parallel)
  const needsAnalysis: Preset[] = [];

  await Promise.all(presets.map(async (preset) => {
    if (signal?.aborted) return;

    const item = items.get(preset.id)!;

    // Already in hot cache?
    const hot = store.getFromHotCache(preset.id);
    if (hot) {
      const freshness = checkFreshness(hot.meta, preset, staleConfig);
      if (freshness.status === 'fresh') {
        item.status = 'done';
        item.progress = 100;
        progress.completed++;
        emit();
        return;
      }
    }

    // Try warm cache
    item.status = 'hydrating';
    emit();

    const meta = await store.getSnapshotMetaForPreset(preset.id);
    if (meta) {
      const freshness = checkFreshness(meta, preset, staleConfig);
      if (freshness.status === 'fresh') {
        // Hydrate from IndexedDB into hot cache
        const hydrated = await store.hydrateSnapshot(preset.id);
        if (hydrated) {
          store.putInHotCache(hydrated);
          item.status = 'done';
          item.progress = 100;
          progress.completed++;
          emit();
          return;
        }
      }
    }

    // Needs fresh analysis
    needsAnalysis.push(preset);
    item.status = 'idle';
    emit();
  }));

  // Phase 2: Analyze stale/missing presets with concurrency control
  if (needsAnalysis.length > 0 && !signal?.aborted) {
    const queue = [...needsAnalysis];
    const running = new Set<string>();

    const runNext = async (): Promise<void> => {
      while (queue.length > 0 && !signal?.aborted) {
        const preset = queue.shift()!;
        const item = items.get(preset.id)!;

        running.add(preset.id);
        item.status = 'analyzing';
        item.progress = 10;
        emit();

        try {
          await analyzeFn(preset);
          item.status = 'done';
          item.progress = 100;
          progress.completed++;
        } catch (err: any) {
          item.status = 'failed';
          item.error = err.message || 'Analysis failed';
        } finally {
          running.delete(preset.id);
          emit();
        }
      }
    };

    // Start up to MAX_CONCURRENT workers
    const workers = Array.from(
      { length: Math.min(MAX_CONCURRENT, needsAnalysis.length) },
      () => runNext()
    );
    await Promise.all(workers);
  }

  return progress;
}

// ==================== Batch Freshness Check ====================

/** Check freshness for all presets at once — used by PresetGrid to show badges */
export async function checkAllFreshness(
  presets: Preset[],
  config?: StaleConfig
): Promise<Map<string, FreshnessInfo>> {
  const result = new Map<string, FreshnessInfo>();
  const metas = await store.getAllSnapshotMetas();

  // Group metas by presetId, pick latest
  const latestMeta = new Map<string, SnapshotMeta>();
  for (const m of metas) {
    const existing = latestMeta.get(m.presetId);
    if (!existing || new Date(m.analysedAt).getTime() > new Date(existing.analysedAt).getTime()) {
      latestMeta.set(m.presetId, m);
    }
  }

  for (const preset of presets) {
    const meta = latestMeta.get(preset.id);
    result.set(preset.id, checkFreshness(meta, preset, config));
  }

  return result;
}

// ==================== Hot Cache Stats ====================

export interface HotCacheStats {
  count: number;
  maxItems: number;
  presetIds: string[];
  totalSizeEstimate: number;
}

export function getHotCacheStats(): HotCacheStats {
  const keys = store.getHotCacheKeys();
  let totalSize = 0;
  for (const key of keys) {
    const snap = store.getFromHotCache(key);
    if (snap) {
      totalSize += snap.meta.uncompressedSize || 0;
    }
  }
  return {
    count: keys.length,
    maxItems: 5,
    presetIds: keys,
    totalSizeEstimate: totalSize,
  };
}
